import React, { Component, Fragment } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router';
import { toast } from 'react-toastify';
import { observe } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import saveAs from 'file-saver';
import { Progress } from 'reactstrap';

import SolverChallengeDetailsCard from '../../components/solver-challenge-details-card/solver-challenge-details-card.js';
import StickySolverChallengeDetailsCard from '../../components/solver-challenge-details-card/sticky-solver-challenge-details-card.js';
import SeekerChallengeDetailsCard from '../../components/seeker-challenge-details-card/seeker-challenge-details-card.js';
import {
	SecondaryButton,
	DownloadButton,
	SocialShareButton,
} from '../../components/ui-toolkit/buttons.js';
import Tag from './shared.component.js';
import EditBar from './edit-bar.js';
import Utils from '../../components/shared/util.js';
import ChallengesService from './challenges.service.js';
import FileService from '../../shared/file.service.js';
import arrowIcon from '../../assets/images/solver-challenge/arrow.svg';
import defaultChallengeImage from '../../assets/images/challenge/challenge-default-image.png';
import Seo from '../../infrastructure/seo.js';

import './challenge-details.page.scss';

class ChallengeDetails extends Component {
	state = {
		showCardOnTop: false,
		userScrolled: false,
		headerRef: React.createRef(),
		showDeleteDraftModal: false,
	};

	componentDidMount() {
		const { generalStore } = this.props;

		generalStore.storeHideDriftChat(true);

		window.scrollTo({
			top: 0,
		});
		this.observer = new IntersectionObserver(entries => {
			const header = entries[0];

			if (!header.isIntersecting) {
				this.setState({ showCardOnTop: true, userScrolled: true });
			} else {
				this.setState({ showCardOnTop: false });
			}
		});

		this.observer.observe(this.state.headerRef.current);
	}

	componentWillUnmount() {
		const { generalStore } = this.props;

		generalStore.storeHideDriftChat(false);
		this.observer.unobserve(this.state.headerRef.current);
	}

	scrollDown = () => {
		window.scrollTo({
			top:
				this.state.headerRef.current.offsetTop +
				this.state.headerRef.current.offsetHeight +
				10,
			behavior: 'smooth',
		});
	};

	toggleShowDeleteDraftModal = () => {
		this.setState(({ showDeleteDraftModal }) => ({
			showDeleteDraftModal: !showDeleteDraftModal,
		}));
	};

	deleteChallengeDraft = () => {
		const { challenge, intl } = this.props;

		ChallengesService.deleteChallengeDraft(challenge.id)
			.then(() => {
				const { history } = this.props;

				Utils.showMessageAlert(
					'delete_draft_modal.draft_deleted',
					toast.TYPE.SUCCESS,
					intl.formatMessage({
						id: 'delete_draft_modal.draft_deleted',
					}),
				);

				history.push('/dashboard');
			})
			.catch(error => {
				Utils.showErrorMessageAlert(error, toast.TYPE.ERROR, intl);
			});
	};

	editChallengeDraft = () => {
		const { challenge, history } = this.props;
		history.push(`/challenges/${challenge.id}/edit`);
	};

	render() {
		const { showDeleteDraftModal } = this.state;
		const {
			challenge,
			isUserSeeker,
			isUserSolver,
			downloadFile,
			downloadingZip,
			downloadZipProgress,
			downloadZip,
			languageStore,
		} = this.props;

		const lang = languageStore.region;

		const attachmentList =
			(challenge &&
				challenge.attachments &&
				challenge.attachments.filter(a => a.lang === lang)) ||
			[];

		return (
			<React.Fragment>
				<img
					className="d-block d-md-none w-100"
					src={
						challenge.backgroundImg && challenge.backgroundImg.url
							? challenge.backgroundImg.url
							: defaultChallengeImage
					}
					alt="background"
				/>

				<div
					className="header"
					style={{
						backgroundImage: `url(${
							challenge.backgroundImg && challenge.backgroundImg.url
								? challenge.backgroundImg.url
								: defaultChallengeImage
						})`,
						backgroundSize: 'cover',
						backgroundPosition: 'center',
					}}
					ref={this.state.headerRef}
				>
					{isUserSolver && (
						<div className="ino-container">
							<SolverChallengeDetailsCard
								id={challenge.id}
								title={
									lang !== 'en_GB' &&
									challenge.translations[lang] &&
									challenge.translations[lang].title
										? challenge.translations[lang].title
										: challenge.title
								}
								award={challenge.award}
								deadline={challenge.deadline}
								solutionStats={challenge.solutionStats}
								status={challenge.status}
								challengeType={challenge.challengeType}
								isExternal={challenge.isExternal}
								externalLink={challenge.externalLink}
								externalLogo={challenge.externalLogo}
							/>
						</div>
					)}

					{!isUserSolver && (
						<div className="ino-container">
							<SeekerChallengeDetailsCard
								title={
									lang !== 'en_GB' &&
									challenge.translations[lang] &&
									challenge.translations[lang].title
										? challenge.translations[lang].title
										: challenge.title
								}
								award={challenge.award}
								deadline={challenge.deadline}
								solutionStats={challenge.solutionStats}
								status={challenge.status}
								challengeType={challenge.challengeType}
								isExternal={challenge.isExternal}
								externalLink={challenge.externalLink}
								externalLogo={challenge.externalLogo}
							/>
						</div>
					)}
				</div>

				{isUserSolver &&
					challenge &&
					challenge.status !== 'EVALUATION' &&
					challenge.status !== 'CLOSED' && (
						<StickySolverChallengeDetailsCard
							id={challenge.id}
							title={
								lang !== 'en_GB' &&
								challenge.translations[lang] &&
								challenge.translations[lang].title
									? challenge.translations[lang].title
									: challenge.title
							}
							award={challenge.award}
							date={moment(challenge.deadline).fromNow()}
							show={this.state.showCardOnTop}
							hide={!this.state.showCardOnTop && this.state.userScrolled}
						/>
					)}

				<div className="arrow d-none d-md-block" onClick={this.scrollDown}>
					<img src={arrowIcon} alt="scroll down" />
				</div>

				<div className="content">
					<h1 className="d-none d-md-block">
						{lang !== 'en_GB' &&
						challenge.translations[lang] &&
						challenge.translations[lang].title
							? challenge.translations[lang].title
							: challenge.title}
					</h1>

					<div className="section">
						<h2>
							<FormattedMessage id="challenge-details.description" />
						</h2>
						<p>
							{lang !== 'en_GB' &&
							challenge.translations[lang] &&
							challenge.translations[lang].description
								? challenge.translations[lang].description
								: challenge.description}
						</p>
					</div>

					{challenge.details && (
						<div className="section">
							<h2>
								<FormattedMessage id="challenge-details.details-of-challenge" />
							</h2>
							<div
								dangerouslySetInnerHTML={{
									__html:
										lang !== 'en_GB' &&
										challenge.translations[lang] &&
										challenge.translations[lang].details
											? challenge.translations[lang].details
											: challenge.details,
								}}
							/>
						</div>
					)}

					{challenge.expectedSolutions && (
						<div className="section">
							<h2>
								<FormattedMessage id="challenge-details.expected-solution" />
							</h2>
							<div
								dangerouslySetInnerHTML={{
									__html:
										lang !== 'en_GB' &&
										challenge.translations[lang] &&
										challenge.translations[lang].expectedSolutions
											? challenge.translations[lang].expectedSolutions
											: challenge.expectedSolutions,
								}}
							/>
						</div>
					)}

					{challenge.minimumRequirements &&
						challenge.minimumRequirements.length > 0 && (
							<div className="section">
								<h2>
									<FormattedMessage id="challenge-details.minimum-requirements" />
								</h2>

								<ul>
									{challenge.minimumRequirements.map(mr => (
										<Fragment key={mr.id}>
											{lang === 'en_GB' && (
												<li>
													<span>{mr.requirement}</span>
												</li>
											)}

											{lang !== 'en_GB' && (
												<li>
													<span>
														{mr.translations && mr.translations[lang]
															? mr.translations[lang]
															: mr.requirement}
													</span>
												</li>
											)}
										</Fragment>
									))}
								</ul>
							</div>
						)}

					<div className="section">
						<h2>
							<FormattedMessage id="challenge-details.areas" />
						</h2>
						{challenge.challengeAreas.map(area => (
							<Tag key={area.id}>{area.name}</Tag>
						))}
					</div>

					<div className="section">
						<h2>
							<FormattedMessage id="challenge-details.deadline" />
						</h2>
						<p>{moment(challenge.deadline).format('LLL')}</p>
					</div>

					{attachmentList.length > 0 && (
						<div className="section">
							<h2>
								<FormattedMessage id="challenge-details.files" />
							</h2>

							<div>
								{attachmentList.map(file => (
									<DownloadButton
										key={file.id}
										onClick={() => !file.downloading && downloadFile(file)}
										fileExtension={file.type}
										fileName={file.name}
										disabled={file.downloading}
										downloading={file.downloading}
										downloadProgress={file.downloadProgress}
									>
										{file.name}
									</DownloadButton>
								))}
							</div>

							{attachmentList.length > 1 && (
								<SecondaryButton
									disabled={downloadingZip}
									onClick={() => !downloadingZip && downloadZip()}
								>
									<FormattedMessage id="solver_challenge_details.download_files" />
									<Progress value={downloadingZip ? downloadZipProgress : 0} />
								</SecondaryButton>
							)}
						</div>
					)}

					<div className="section social-links">
						<SocialShareButton type="facebook" />
						<SocialShareButton type="linkedin" />
						<SocialShareButton type="twitter" />
						<SocialShareButton type="email" />
						<SocialShareButton type="copy" />
					</div>
				</div>

				{challenge.status !== 'EVALUATION' &&
					challenge.status !== 'CLOSED' &&
					isUserSeeker &&
					challenge.editableByCurrentSeeker && (
						<EditBar
							isDraft={challenge.status === 'DRAFT'}
							showDeleteDraftModal={showDeleteDraftModal}
							toggleShowDeleteDraftModal={this.toggleShowDeleteDraftModal}
							deleteDraft={this.deleteChallengeDraft}
							editDraft={this.editChallengeDraft}
						/>
					)}
			</React.Fragment>
		);
	}
}

const ChallengeDetailsWrapper = withRouter(
	injectIntl(
		inject('generalStore', 'languageStore')(observer(ChallengeDetails)),
	),
);

class ChallengeDetailsPage extends Component {
	state = {
		challenge: null,
		downloadingZip: false,
		downloadZipProgress: 0,
	};

	componentDidMount() {
		const { userStore } = this.props;

		if (!userStore.isFetching) {
			this.getChallenge();
		}

		this.isFetchingDisposer = observe(
			userStore,
			'isFetching',
			({ newValue }) => {
				if (!newValue) {
					this.getChallenge();
				}
			},
		);
	}

	componentWillUnmount() {
		this.isFetchingDisposer();
	}

	getChallenge = () => {
		const {
			match: {
				params: { challengeId },
			},
			generalStore: { startLoading, stopLoading },
			languageStore,
			intl,
		} = this.props;
		const lang = languageStore.region;

		startLoading();
		ChallengesService.getChallengeDetails({ id: challengeId, lang })
			.then(challenge => {
				this.setState(
					{
						challenge,
					},
					() => {
						stopLoading();
					},
				);
			})
			.catch(error => {
				stopLoading();
				Utils.showErrorMessageAlert(error, toast.TYPE.ERROR, intl);
			});
	};

	handleProgress = (file, progress) => {
		const { challenge } = this.state;
		const uploadedFile = challenge.attachments.filter(f => f.id === file.id);

		uploadedFile[0].downloading = progress.loaded !== progress.total;
		uploadedFile[0].downloadProgress = Math.floor(
			(progress.loaded * 100) / progress.total,
		);

		this.setState({
			challenge,
		});
	};

	downloadFile = file => {
		FileService.downloadFile(file, this.handleProgress)
			.then(fileBlob => {
				saveAs(fileBlob, file.name);
			})
			.catch(error => {
				const { intl } = this.props;

				Utils.showErrorMessageAlert(error, toast.TYPE.ERROR, intl);
			});
	};

	handleZipProgress = (file, progress) => {
		this.setState({
			downloadingZip: progress.loaded !== progress.total,
			downloadZipProgress: Math.floor((progress.loaded * 100) / progress.total),
		});
	};

	downloadZip = () => {
		const { challenge } = this.state;
		const {
			languageStore: { region },
		} = this.props;

		const url = `${process.env.REACT_APP_API_URL}/api/challenge/${challenge.id}/attachment/all?lang=${region}`;

		FileService.downloadFile({ url }, this.handleZipProgress)
			.then(fileBlob => {
				saveAs(fileBlob, challenge.title.replace(/ /g, '_'));
			})
			.catch(error => {
				const { intl } = this.props;

				Utils.showErrorMessageAlert(error, toast.TYPE.ERROR, intl);
			});
	};

	render() {
		const { challenge, downloadingZip, downloadZipProgress } = this.state;
		const { userStore, intl, generalStore } = this.props;
		const {
			addresses,
			phones,
			generalData: { logo },
		} = generalStore;

		if (!challenge || userStore.isFetching) return null;

		return (
			<div id="challenge-detail-page">
				<Seo
					title={
						challenge.title ||
						intl.formatMessage({ id: 'seo.challenge_detail.title' })
					}
					description={
						challenge.description ||
						challenge.title ||
						intl.formatMessage({ id: 'seo.challenge_detail.title' })
					}
					microdata={`{
									"@context": "https://schema.org",
									"@graph": [
										{
											"@type": "Service",
											"name": "${challenge.title || ''}",
											"description": "${challenge.description || ''}",
											"award": "${challenge.award.amount || ''}",
											"hoursAvailable": {
												"@type": "OpeningHoursSpecification",
												"validThrough": "${challenge.deadline || ''}"
											},
											"serviceType": [
												${challenge.challengeAreas.length > 0 &&
													challenge.challengeAreas.map(
														area => `
													"${area.name}"
												`,
													)}
											],
											${
												challenge.image
													? `
												"image": "${challenge.image}",
											`
													: ''
											}
											"url": "${process.env.REACT_APP_PUBLIC_URL}/challenges/${challenge.id}"
										},
										{
											"@type": "WebApplication",
											"name": "Inocrowd",
											"url": "${process.env.REACT_APP_PUBLIC_URL}",
											"applicationCategory": "BusinessApplication",
											"operatingSystem": "Windows, Linux, OSX, iOS, Android",
											"sameAs": [
												"${process.env.REACT_APP_WEBSITE_URL}",
												"https://www.facebook.com/InoCrowd/",
												"https://www.linkedin.com/company/inocrowd",
												"https://twitter.com/InoCrowd",
												"https://www.instagram.com/inocrowd",
												"https://www.youtube.com/channel/UC8JotJA9Txz_6NQXWuEwdvQ"
											],
											"provider": {
												"@type": "Organization",
												"name": "Inocrowd",
												${addresses.length > 0 &&
													`
													"address":  "${addresses[0].description.replace(/\n/g, ' ')}",
												`}
												${phones.length > 0 &&
													`
													"contactPoint": {
														"@type": "ContactPoint",
														"telephone": "${phones[0].phoneNumber}",
														"contactType": "customer service"
													},
													"telephone": "${phones[0].phoneNumber}",
												`}
												${logo &&
													`
													"logo": "${process.env.REACT_APP_CMS_URL}${logo.path}"
												`}
											}
										},
										{
											"@type": "BreadcrumbList",
											"itemListElement": [
												{
													"@type": "ListItem",
													"name": "Inocrowd",
													"position": 1,
													"item": "${process.env.REACT_APP_PUBLIC_URL}"
												}
											]
										},
										{
											"@type": "BreadcrumbList",
											"itemListElement": [
												{
													"@type": "ListItem",
													"name": "${challenge.title}",
													"position": 2,
													"item": "${process.env.REACT_APP_PUBLIC_URL}/challenges/${challenge.id}"
												}
											]
										}
									]
								}`}
				/>

				<ChallengeDetailsWrapper
					challenge={challenge}
					isUserSeeker={userStore.profile.type === 'SEEKER'}
					isUserSolver={userStore.profile.type === 'SOLVER'}
					downloadFile={this.downloadFile}
					downloadingZip={downloadingZip}
					downloadZipProgress={downloadZipProgress}
					downloadZip={this.downloadZip}
				/>
			</div>
		);
	}
}

export default injectIntl(
	inject('userStore', 'generalStore', 'languageStore')(
		observer(ChallengeDetailsPage),
	),
);
